import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Link
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ResetIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../services/api';

const MessageTemplates = () => {
  const [templates, setTemplates] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedValue, setEditedValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [userSettings, setUserSettings] = useState({
    name: '',
    companyName: '',
    industry: '',
    reviewLink: ''
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsChecked, setSettingsChecked] = useState(false);

  // Template display names and order for better readability (in Swedish)
  const templateNames = {
    'INITIAL_FEEDBACK': 'Första meddelandet',
    'REVIEW_REQUEST': 'Recensionsförfrågan',
    'COMPLETED': 'Tackmeddelande',
    'ERROR': 'Felmeddelande'
  };
  
  // Order of templates in the flow
  const templateOrder = ['INITIAL_FEEDBACK', 'REVIEW_REQUEST', 'COMPLETED', 'ERROR'];

  // Available template variables
  const templateVariables = {
    'INITIAL_FEEDBACK': [],
    'REVIEW_REQUEST': ['review_link'],
    'COMPLETED': [],
    'ERROR': []
  };

  // Add safeguard for templates state
  useEffect(() => {
    // If templates somehow becomes null or undefined after being set
    if (templates === null || templates === undefined) {
      console.error('CRITICAL: templates state became null or undefined!');
      console.trace('Stack trace for templates becoming null');
      
      // Auto-correct by setting to empty object
      console.log('Auto-correcting templates state to empty object');
      setTemplates({});
    }
  }, [templates]);

  // Fetch templates on component mount
  useEffect(() => {
    console.log('MessageTemplates component mounted, fetching data...');
    fetchUserSettings();  // Fetch settings first
  }, []);

  // Update to fetch templates after settings are loaded
  useEffect(() => {
    // Only fetch templates after we've checked settings
    if (settingsChecked) {
      console.log('Settings checked, now fetching templates');
      fetchTemplates();
    }
  }, [settingsChecked]);

  // Fetch user settings from the API
  const fetchUserSettings = async () => {
    try {
      console.log('Fetching user settings...');
      const response = await api.get('/settings');
      console.log('User settings response:', response.data);
      const settingsData = {
        name: response.data.name || '',
        companyName: response.data.company?.name || '',
        industry: response.data.company?.industry || '',
        reviewLink: response.data.company?.reviewLink || ''
      };
      
      setUserSettings(settingsData);
      console.log('User settings updated:', settingsData);
      
      // Always show settings modal if ANY required settings are missing
      const hasMissingSettings = !settingsData.name || !settingsData.companyName || !settingsData.reviewLink;
      setSettingsModalOpen(hasMissingSettings);
      setSettingsChecked(true); // Mark that we've checked settings
    } catch (err) {
      console.error('Error fetching user settings:', err);
      setSettingsChecked(true); // Still mark as checked even on error
    }
  };

  const fetchTemplates = async () => {
    console.log('fetchTemplates called, setting loading to true');
    setLoading(true);
    try {
      console.log('Making API call to fetch templates...');
      const response = await api.get('/api/templates');
      console.log('Templates API response:', response);
      
      // Add more detailed logging to debug the response
      console.log('Response data type:', typeof response.data);
      
      // First check if response data exists and is not null
      if (response.data !== null && response.data !== undefined && typeof response.data === 'object') {
        // Ensure we never set templates to null/undefined
        const templateData = response.data;
        
        // Check if we have user settings and if templates need personalization
        const needsPersonalization = Object.values(templateData).some(template => 
          template.includes('{user_name}') || 
          template.includes('{company_name}') || 
          template.includes('{industry}') || 
          template.includes('{review_link}')
        );
        
        if (needsPersonalization && userSettings && 
            (userSettings.name || userSettings.companyName || userSettings.reviewLink)) {
          console.log('Templates need personalization and user settings available - personalizing templates');
          await personalizeTemplates(userSettings);
        } else {
          if (needsPersonalization) {
            // Show warning that templates aren't personalized
            setError('Mallar innehåller variabler som inte har ersatts. Kontrollera dina inställningar.');
          } else {
            setError(null);
          }
          setTemplates(templateData);
        }
      } else {
        console.warn('API returned null or invalid data format, using default templates');
        // Use default templates when API returns null
        const defaultTemplates = {
          "INITIAL_FEEDBACK": "Hej! Tack för möjligheten. Hur upplevde du vår service?",
          "REVIEW_REQUEST": "Vi uppskattar din positiva feedback! Skulle du kunna lämna en recension på {review_link}? Det skulle betyda mycket för oss.",
          "COMPLETED": "Tack för din feedback! Vi uppskattar din åsikt.",
          "ERROR": "Tyvärr, något gick fel. Vänligen försök igen senare."
        };
        console.log('Setting templates to default templates:', defaultTemplates);
        setTemplates(defaultTemplates);
        setError('Kunde inte hämta anpassade mallar. Visar standardmallar.');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      console.log('Current templates state:', templates);
      // Set default templates on error instead of empty object
      const defaultTemplates = {
        "INITIAL_FEEDBACK": "Hej! Tack för möjligheten. Hur upplevde du vår service?",
        "REVIEW_REQUEST": "Vi uppskattar din positiva feedback! Skulle du kunna lämna en recension på {review_link}? Det skulle betyda mycket för oss.",
        "COMPLETED": "Tack för din feedback! Vi uppskattar din åsikt.",
        "ERROR": "Tyvärr, något gick fel. Vänligen försök igen senare."
      };
      console.log('Setting templates to default templates due to error');
      setTemplates(defaultTemplates);
      setError('Det gick inte att ladda meddelandemallar. Visar standardmallar.');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleOpenEdit = (key, value) => {
    setEditingTemplate(key);
    setEditedValue(value);
  };

  const handleCloseEdit = () => {
    setEditingTemplate(null);
    setEditedValue('');
  };

  const handleCloseSettingsModal = () => {
    // Check if required settings are filled
    const hasMissingSettings = !userSettings.name || !userSettings.companyName || !userSettings.reviewLink;
    
    // Only allow closing if all required settings are filled
    if (!hasMissingSettings) {
      setSettingsModalOpen(false);
    } else {
      setSettingsError('Du måste fylla i alla obligatoriska fält innan du kan fortsätta.');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await api.post('/api/templates', {
        message_key: editingTemplate,
        message_value: editedValue
      });
      
      // Update local state
      setTemplates(prev => ({
        ...prev,
        [editingTemplate]: editedValue
      }));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Mall uppdaterad!',
        severity: 'success'
      });
      
      handleCloseEdit();
    } catch (err) {
      console.error('Error updating template:', err);
      setSnackbar({
        open: true,
        message: 'Det gick inte att uppdatera mallen. Försök igen.',
        severity: 'error'
      });
    }
  };

  const handleResetTemplate = async (key) => {
    try {
      await api.delete(`/api/templates/${key}`);
      
      // Refresh templates to get the default value
      await fetchTemplates();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Mall återställd till standard!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error resetting template:', err);
      setSnackbar({
        open: true,
        message: 'Det gick inte att återställa mallen. Försök igen.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Helper function to insert a variable into the current template
  const insertVariable = (variable) => {
    const insertText = `{${variable}}`;
    const newValue = editedValue + insertText;
    setEditedValue(newValue);
  };

  // Helper function to get template descriptions
  const getTemplateDescription = (key) => {
    switch(key) {
      case 'INITIAL_FEEDBACK':
        return 'Detta är det första meddelandet som skickas till kunden för att be om feedback.';
      case 'REVIEW_REQUEST':
        return 'Detta meddelande skickas när kunden ger positiv feedback, för att be om en recension.';
      case 'COMPLETED':
        return 'Detta är tackmeddelandet som skickas i slutet av konversationen.';
      case 'ERROR':
        return 'Detta meddelande visas om ett fel uppstår.';
      default:
        return '';
    }
  };

  // Navigate to settings page
  const goToSettings = () => {
    window.location.href = '/settings';
  };

  // Add state for inline settings editing
  const [editableSettings, setEditableSettings] = useState({
    name: '',
    companyName: '',
    industry: '',
    reviewLink: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  // Update editable settings when user settings are fetched
  useEffect(() => {
    console.log('userSettings changed, updating editableSettings:', userSettings);
    setEditableSettings({
      name: userSettings.name || '',
      companyName: userSettings.companyName || '',
      industry: userSettings.industry || '',
      reviewLink: userSettings.reviewLink || ''
    });
  }, [userSettings]);

  // Handle settings form change
  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setEditableSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save settings directly from the modal
  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setSettingsError(null);
      
      // Validate required fields
      if (!editableSettings.name || !editableSettings.companyName || !editableSettings.reviewLink) {
        setSettingsError('Du måste fylla i alla obligatoriska fält innan du kan fortsätta.');
        return;
      }
      
      // Format data for settings API - use editableSettings instead of userSettings
      const formattedData = {
        name: editableSettings.name,
        companyName: editableSettings.companyName,
        industry: editableSettings.industry,
        reviewLink: editableSettings.reviewLink.startsWith('http') ? 
          editableSettings.reviewLink : 
          `https://${editableSettings.reviewLink}`
      };
      
      console.log('Saving settings from modal:', formattedData);
      
      // Save the user settings
      await api.post('/settings', formattedData);
      
      // Update local state
      setUserSettings(formattedData);
      
      // Always personalize templates after saving settings
      console.log('Settings saved, now personalizing templates with new settings:', formattedData);
      await personalizeTemplates(formattedData);
      
      setSettingsModalOpen(false);
      setSnackbar({
        open: true,
        message: 'Inställningar sparade',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving settings:', err);
      setSettingsError('Ett fel uppstod när inställningarna skulle sparas');
      setSnackbar({
        open: true,
        message: 'Ett fel uppstod när inställningarna skulle sparas',
        severity: 'error'
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Function to personalize templates with user settings
  const personalizeTemplates = async (settings) => {
    try {
      console.log('Personalizing templates with settings:', settings);
      
      // Make sure all required fields are present
      const personalizationData = {
        name: settings.name || '',
        companyName: settings.companyName || '',
        industry: settings.industry || '',
        reviewLink: settings.reviewLink || ''
      };
      
      console.log('Sending personalization request with data:', personalizationData);
      const response = await api.post('/api/personalize', personalizationData);
      console.log('Templates personalization response:', response.data);
      
      // Check for server-side errors
      if (response.data?.error) {
        console.warn(`Server returned error: ${response.data.error}`);
        if (response.data.error === 'missing_settings') {
          setError('Personalisering kräver att du fyller i ditt namn och företagsnamn. Gå till inställningar för att uppdatera dessa.');
          setSettingsModalOpen(true); // Open settings modal to fix the missing settings
        } else {
          setError('Ett fel uppstod vid personalisering av mallar. Använder standardmallar istället.');
        }
        
        // Still use the templates we got
        if (response.data?.templates) {
          setTemplates(response.data.templates);
        }
        return;
      }
      
      // If the response includes templates, use them directly
      if (response.data?.templates && typeof response.data.templates === 'object') {
        console.log('Received personalized templates from server:', response.data.templates);
        
        // Check if any templates still contain variable placeholders
        const templatesWithVariables = Object.entries(response.data.templates).filter(([key, value]) => {
          return value.includes('{user_name}') || 
                 value.includes('{company_name}') || 
                 value.includes('{industry}') || 
                 value.includes('{review_link}');
        });
        
        if (templatesWithVariables.length > 0) {
          console.warn('Some templates still contain variables after personalization:', 
            templatesWithVariables.map(([key]) => key));
          
          // Show error message instead of manually personalizing
          setError('Kunde inte personalisera några mallar. Du kan fortsätta använda standardmallarna, eller kontakta support.');
          // Still use the templates we got, even with variables
          setTemplates(response.data.templates);
        } else {
          // Templates are already personalized
          console.log('Templates are fully personalized');
          setError(null); // Clear any previous errors
          setTemplates(response.data.templates);
        }
      } else {
        // Otherwise, fetch templates again
        console.warn('No templates in personalization response, fetching templates instead');
        setError('Personalisering misslyckades. Använder standardmallar istället.');
        fetchTemplates();
      }
    } catch (err) {
      console.error('Error personalizing templates:', err);
      console.error('Error details:', err.response?.data);
      
      // Show error message
      setError('Ett fel uppstod vid personalisering av mallar. Använder standardmallar istället.');
      
      // Still try to fetch templates as fallback
      fetchTemplates();
    }
  };

  if (loading) {
    console.log('Rendering loading state');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Meddelandemallar
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Anpassa meddelandena som skickas till dina kunder. Du kan använda variabler som {'{review_link}'} där det är tillämpligt.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Tips: Företagets recensionslänk från inställningar används automatiskt när du använder {'{review_link}'} i recension förfrågan mallen.
        </Typography>
      </Alert>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Konversationsflöde
      </Typography>
      
      <Grid container spacing={3}>
        {templateOrder.map((key) => {
          // Check if we have the template from the API
          const hasTemplate = templates && typeof templates[key] === 'string';
          
          // Always render the template card
          return (
            <Grid item xs={12} key={key}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {templateNames[key] || key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {getTemplateDescription(key)}
                  </Typography>
                  {templateVariables[key].length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Tillgängliga variabler:
                      </Typography>
                      {templateVariables[key].map(variable => (
                        <Chip 
                          key={variable} 
                          label={`{${variable}}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ mr: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  {!hasTemplate ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Laddar mall...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {templates[key]}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenEdit(key, hasTemplate ? templates[key] : '')}
                    disabled={!hasTemplate}
                  >
                    Redigera
                  </Button>
                  <Button 
                    startIcon={<ResetIcon />} 
                    color="secondary"
                    onClick={() => handleResetTemplate(key)}
                    disabled={!hasTemplate}
                  >
                    Återställ till standard
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Redigera: {editingTemplate && templateNames[editingTemplate]}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="template"
            label="Meddelandetext"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            sx={{ mt: 1 }}
          />
          {editingTemplate && templateVariables[editingTemplate].length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Klicka för att infoga variabel:
              </Typography>
              {templateVariables[editingTemplate].map(variable => (
                <Button 
                  key={variable}
                  variant="outlined" 
                  size="small" 
                  onClick={() => insertVariable(variable)}
                  sx={{ mr: 1, mb: 1 }}
                >
                  {`{${variable}}`}
                </Button>
              ))}
            </Box>
          )}
          {editingTemplate === 'REVIEW_REQUEST' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Använd {'{review_link}'} för att infoga recensionslänken som är konfigurerad i företagsinställningar. 
                Om ingen länk är konfigurerad används standardlänken.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Avbryt</Button>
          <Button onClick={handleSaveTemplate} variant="contained">Spara</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Modal */}
      <Dialog 
        open={settingsModalOpen} 
        onClose={handleCloseSettingsModal} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown // Prevent closing with Escape key
      >
        <DialogTitle>
          Inställningar Krävs
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SettingsIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
            <Typography variant="h6">
              Komplettera dina uppgifter
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph>
            För att använda meddelandemallar behöver du fylla i följande obligatoriska uppgifter:
          </Typography>
          
          {settingsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {settingsError}
            </Alert>
          )}
          
          <Box component="form" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Personlig Information
            </Typography>
            <TextField
              required
              fullWidth
              label="Ditt namn"
              name="name"
              value={editableSettings.name}
              onChange={handleSettingsChange}
              margin="normal"
              placeholder="Ditt namn"
              size="small"
              error={!editableSettings.name}
              helperText={!editableSettings.name ? "Obligatoriskt fält" : ""}
            />
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Företagsinformation
            </Typography>
            <TextField
              required
              fullWidth
              label="Företagsnamn"
              name="companyName"
              value={editableSettings.companyName}
              onChange={handleSettingsChange}
              margin="normal"
              placeholder="Ditt företags namn"
              size="small"
              error={!editableSettings.companyName}
              helperText={!editableSettings.companyName ? "Obligatoriskt fält" : ""}
            />
            <TextField
              fullWidth
              label="Bransch"
              name="industry"
              value={editableSettings.industry}
              onChange={handleSettingsChange}
              margin="normal"
              placeholder="Din bransch"
              size="small"
            />
            <TextField
              required
              fullWidth
              label="Recensionslänk"
              name="reviewLink"
              value={editableSettings.reviewLink}
              onChange={handleSettingsChange}
              margin="normal"
              placeholder="https://exempel.se/recension"
              size="small"
              error={!editableSettings.reviewLink}
              helperText={!editableSettings.reviewLink ? "Obligatoriskt fält. Används i {review_link} variabeln" : ""}
            />
          </Box>
          
          <Alert severity="warning" sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body2">
              Du måste spara dessa inställningar innan du kan använda meddelandemallar.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveSettings}
            disabled={savingSettings}
            fullWidth
          >
            {savingSettings ? 'Sparar...' : 'Spara inställningar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageTemplates; 